<script setup lang="ts">
defineProps<{
  tone: 'offline' | 'syncing' | 'error' | 'update'
  icon?: string
  spinner?: boolean
}>()

defineSlots<{
  default(): unknown
  actions(): unknown
}>()

const toneIcons = {
  offline: 'mdi-wifi-off',
  syncing: 'mdi-sync',
  error: 'mdi-alert-circle-outline',
  update: 'mdi-update',
}
</script>

<template>
  <v-alert
    variant="tonal"
    class="fb-banner"
    :class="`fb-banner--${tone}`"
    :role="tone === 'error' ? 'alert' : 'status'"
  >
    <template #prepend>
      <v-progress-circular v-if="spinner" indeterminate :size="20" aria-hidden="true" />
      <v-icon v-else :icon="icon || toneIcons[tone]" aria-hidden="true" />
    </template>

    <div class="fb-banner__body">
      <div class="fb-banner__text">
        <slot />
      </div>
      <div v-if="$slots.actions" class="fb-banner__actions">
        <slot name="actions" />
      </div>
    </div>
  </v-alert>
</template>

<style scoped>
.fb-banner {
  border-radius: 18px;
}

/* The tone pairs supply the full tint; Vuetify's underlay would tint them again. */
.fb-banner :deep(.v-alert__underlay) {
  opacity: 0;
}

.fb-banner--offline {
  background: var(--fb-status-waiting-bg);
  color: #5C3D00;
}

.fb-banner--syncing {
  background: var(--fb-status-todo-bg);
  color: var(--fb-status-todo-fg);
}

.fb-banner--error {
  background: var(--fb-status-sent-back-bg);
  color: var(--fb-status-sent-back-fg);
}

.fb-banner--update {
  background: #6C3CF0;
  color: #FFFFFF;
  box-shadow: 0 4px 0 #4A23B8;
}

.fb-banner__body {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 16px;
}

.fb-banner__text {
  flex: 1 1 200px;
  min-width: 0;
  overflow-wrap: anywhere;
  font-size: 15px;
  font-weight: 800;
}

.fb-banner__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
  margin-inline-start: auto;
  max-width: 100%;
}

.fb-banner__actions :deep(.v-btn),
.fb-banner__actions :deep(button) {
  min-height: 44px;
}
</style>
